import { usePatientCareTeamMembers } from '@ctw/shared/api/fhir/care-team';
import type { CareTeamPractitionerModel } from '@ctw/shared/api/fhir/models/careteam-practitioner';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { useMainContentHeader } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { HotloadGate } from '@ctw/shared/content/hotload-gate';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { useMemo } from 'react';
import { ResourceTableActions } from '../resource/resource-table-actions';
import { getDateRangeView } from '../resource/view-date-range';
import { patientCareTeamColumns } from './helpers/columns';
import { careTeamSortOptions, defaultCareTeamSort } from './helpers/sorts';

export const PatientCareTeam = withErrorBoundary({
  boundaryName: 'PatientCareTeam',
  Component: () => {
    const { patient } = usePatientContext();

    return (
      <HotloadGate upid={patient.UPID} resourceTypes={['CareTeam']}>
        <PatientCareTeamContent />
      </HotloadGate>
    );
  },
});

const PatientCareTeamContent = () => {
  const { requestContext } = useCTW();
  const { openResourceDrawer } = useResourceDrawer();
  const patientCareTeamQuery = usePatientCareTeamMembers();
  const { viewOptions, allTime } = useMemo(
    () => getDateRangeView<CareTeamPractitionerModel>('effectiveStartDate'),
    [],
  );

  const { data, setSort, setViewOption, defaultSort, defaultView } = useFilteredSortedData({
    cacheKey: 'patient-careteam',
    defaultSort: defaultCareTeamSort,
    viewOptions,
    records: patientCareTeamQuery.data,
    defaultView: allTime.display,
  });
  const isEmptyQuery = patientCareTeamQuery.data && patientCareTeamQuery.data.length === 0;
  const hasZeroFilteredRecords = !isEmptyQuery && data.length === 0;

  useMainContentHeader(
    'PatientCareTeamContentHeader',
    () => (
      <ResourceTableActions
        sortOptions={{
          defaultSort,
          options: careTeamSortOptions,
          onChange: setSort,
        }}
        viewOptions={{
          onChange: setViewOption,
          options: viewOptions,
          defaultView,
        }}
      />
    ),
    [defaultSort, careTeamSortOptions, viewOptions, defaultView],
  );

  return (
    <div>
      <ResourceTable
        title="Care Team"
        titleVisuallyHidden={true}
        isLoading={patientCareTeamQuery.isLoading}
        data={data}
        emptyMessage={
          <EmptyPatientTable
            hasZeroFilteredRecords={hasZeroFilteredRecords}
            resourceName="careteam"
          />
        }
        columns={patientCareTeamColumns(requestContext.builderId)}
        onRowClick={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
      />
    </div>
  );
};
